import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type {
  CSSProperties,
  ComponentProps,
  FC,
  PropsWithChildren,
} from 'react';
import { useEffect, useState } from 'react';
import { FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconUsers } from '@sorare/core/src/atoms/icons/system/iconUsers.ts';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS } from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import { getSafePreviousLocation } from '@sorare/core/src/lib/navigation/getSafePreviousLocation';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import {
  FOOTBALL_TABS,
  NBA_TABS,
} from '#src/components/contender/ContenderDrawer/ContenderDrawerContent/index.tsx';
import { ContenderDrawer } from '#src/components/contender/ContenderDrawer/index.tsx';
import { ContenderDrawerTabs } from '#src/components/contender/ContenderDrawer/types.ts';
import { FixtureStatus } from '#src/components/fixture/FixtureStatus/index.tsx';
import { DivisionChangeBanner } from '#src/components/lineup/Contender/DivisionChangeBanner/index.tsx';
import { LineupHeader } from '#src/components/lineup/LineupHeader/index.tsx';
import { So5Lineup } from '#src/components/lineup/So5Lineup/index.tsx';
import { getLineupDisplayName } from '#src/lib/lineup/getLineupDisplayName.ts';

import { TeamLineupFooter } from './TeamLineupFooter/index.tsx';
import type { TeamLineupLayout_so5LeaderboardContender } from './__generated__/index.graphql.ts';

const Root = styled.div`
  display: grid;
  min-height: var(--100vh);
  align-items: stretch;
  background: var(--bg), var(--c-black);
  background-size: clamp(650px, 100%, 100%) auto;
  background-position: center 0;
  background-repeat: no-repeat;
  @media ${laptopAndAbove} {
    grid-template-columns: 1fr var(--drawer-width);
    background-size: clamp(
        100px,
        calc(100% - var(--drawer-width) + 300px),
        1300px
      )
      100%;
    background-position: calc(50% - var(--drawer-width) / 2) 1.5vh;
  }
`;
const Main = styled(Vertical).attrs({ gap: 2 })`
  position: relative;
`;
const Header = styled(SBHorizontal)`
  padding: var(--double-unit) 0;
`;
const Children = styled(Container)`
  width: 100%;
  margin: auto;
`;

const drawerWidth = '420px';

type Props = {
  so5LeaderboardContender: TeamLineupLayout_so5LeaderboardContender;
  backgroundUrl?: string;
} & Pick<ComponentProps<typeof ContenderDrawer>, 'onClickGame'>;

export const TeamLineupLayout: FC<PropsWithChildren<Props>> = ({
  so5LeaderboardContender,
  backgroundUrl,
  onClickGame,
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { sport, generateSportContextPath } = useSportContext();
  const { setPageMetadata } = useSeoContext();
  const intl = useIntl();
  const { formatMessage } = intl;
  const isLaptopAndAbove = useIsLaptop();
  const track = useEvents();
  const [drawerOpened, setDrawerOpened] = useState(false);

  const { so5Lineup, so5Leaderboard, managerTeam } = so5LeaderboardContender;
  const ranking = so5Lineup?.so5Rankings.at(0);

  const [tab, setTab] = useState<ContenderDrawerTabs>(
    sport === Sport.FOOTBALL ? FOOTBALL_TABS[0] : NBA_TABS[0]
  );

  const safePreviousLocation = getSafePreviousLocation(
    generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_LINEUPS, {
      params: {
        eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
        fixture: so5Leaderboard.so5Fixture.slug,
      },
    }),
    -1
  );

  useEffect(
    () =>
      setPageMetadata(
        `${getLineupDisplayName({
          so5Leaderboard,
          so5Lineup,
          managerTeam,
          addLeaguePrefix: true,
        })}`,
        {
          img: so5Lineup?.socialPictureUrls.post,
        }
      ),
    [setPageMetadata, so5Lineup, so5Leaderboard, managerTeam]
  );

  const isMyContender =
    (managerTeam?.user.slug || so5Lineup?.user.slug) === currentUser?.slug;

  return (
    <Root
      style={
        {
          '--bg': `url(${backgroundUrl})`,
          '--drawer-width': drawerWidth,
        } as CSSProperties
      }
    >
      <Main>
        <Container>
          <Header>
            <Horizontal gap={0}>
              <IconButton
                size="medium"
                to={safePreviousLocation}
                color="transparent"
                icon={iconX}
                title={formatMessage(glossary.close)}
              />
              <LineupHeader
                showRenameButton={sport === Sport.FOOTBALL && !!isMyContender}
                so5LeaderboardContender={so5LeaderboardContender}
              />
            </Horizontal>
            <Horizontal gap={2}>
              <FixtureStatus
                so5Leaderboard={so5Leaderboard}
                so5Lineup={so5Lineup || undefined}
              />
              {!!isLaptopAndAbove && (
                <Horizontal>
                  <FontAwesomeIcon
                    icon={iconUsers}
                    size="xs"
                    color="var(--c-nd-700)"
                  />
                  <BodyS as="p" color="var(--c-nd-700)">
                    <FormattedNumber value={so5Leaderboard.so5LineupsCount} />
                  </BodyS>
                </Horizontal>
              )}
            </Horizontal>
          </Header>
        </Container>
        {!isLaptopAndAbove && !!ranking?.eligibleDivisionChange && (
          <DivisionChangeBanner
            divisionChange={ranking.eligibleDivisionChange}
          />
        )}
        <Children>
          <So5Lineup
            fillHeightPercent={0.7}
            so5LeaderboardContender={so5LeaderboardContender}
            lineupStyle="spread"
            displayLiveEdit
          />
        </Children>
        <TeamLineupFooter
          so5LeaderboardContender={so5LeaderboardContender}
          onClickPrizePool={() => {
            setTab(ContenderDrawerTabs.PRIZE_POOL);
            setDrawerOpened(true);
          }}
          onClickBonus={() => {
            setTab(ContenderDrawerTabs.BONUS);
            setDrawerOpened(true);
            track('Click Lineup Bonus', {
              so5LineupId: so5Lineup?.id || '',
              rarity: so5Leaderboard.mainRarityType || 'mix',
            });
          }}
        />
      </Main>
      <ContenderDrawer
        width={drawerWidth}
        open={isLaptopAndAbove || drawerOpened}
        contender={so5LeaderboardContender}
        transparent={isLaptopAndAbove}
        onClose={!isLaptopAndAbove ? () => setDrawerOpened(false) : undefined}
        tab={tab}
        onChangeTab={setTab}
        onClickGame={onClickGame}
      />
    </Root>
  );
};

TeamLineupLayout.fragments = {
  so5LeaderboardContender: gql`
    fragment TeamLineupLayout_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        mainRarityType
        so5LeaderboardGroup {
          slug
        }
        ...FixtureStatus_so5Leaderboard
        ...getLineupDisplayName_so5Leaderboard
      }
      so5Lineup {
        id
        name
        socialPictureUrls {
          post
        }
        so5Rankings {
          id
          eligibleDivisionChange
        }
        user {
          slug
        }
        ...getLineupDisplayName_so5Lineup
        ...FixtureStatus_so5Lineup
      }
      managerTeam {
        id
        divisionChangingSo5Lineup {
          id
          so5Rankings {
            id
            eligibleDivisionChange
          }
        }
        user {
          slug
        }
        ...getLineupDisplayName_managerTeam
      }
      ...LobbyLineupHeader_so5LeaderboardContender
      ...TeamLineupFooter_so5LeaderboardContender
      ...ContenderDrawer_so5LeaderboardContender
      ...So5Lineup_so5LeaderboardContender
    }
    ${LineupHeader.fragments.so5LeaderboardContender}
    ${TeamLineupFooter.fragments.so5LeaderboardContender}
    ${getLineupDisplayName.fragments.so5Leaderboard}
    ${getLineupDisplayName.fragments.so5Lineup}
    ${getLineupDisplayName.fragments.managerTeam}
    ${FixtureStatus.fragments.so5Leaderboard}
    ${FixtureStatus.fragments.so5Lineup}
    ${ContenderDrawer.fragments.so5LeaderboardContender}
    ${So5Lineup.fragments.so5LeaderboardContender}
    ${FixtureStatus.fragments.so5Leaderboard}
  ` as TypedDocumentNode<TeamLineupLayout_so5LeaderboardContender>,
};
